<script setup>
import { computed } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { printPage } from '@utils';

import { XIcon, XLink, XButton } from '@clicksign/excalibur';
import VueQrcode from '@chenfengyuan/vue-qrcode';
import CompanyInfo from '@components/CompanyInfo.vue';
import CertifyInfo from '@components/CertifyInfo.vue';

const props = defineProps({
  parentUrl: {
    type: String,
    default: window.location.origin,
  },
});

const store = useStore();
const { t } = useI18n();

const validDocument = computed(() => store.getters['fileUpload/validDocument']);

const fillPasswordUrl = computed(() => {
  const encriptedAccessToken = btoa(validDocument.value.access_token);

  if (props.parentUrl === window.location.origin) {
    return `${props.parentUrl}/validate_accesses?senha=${encriptedAccessToken}`;
  }

  return `${props.parentUrl}?senha=${encriptedAccessToken}`;
});

function handlePagePrint(e) {
  printPage(e);
}
</script>

<template>
  <section :class="$style.documentValidatorResult">
    <header :class="$style.header">
      <XLink
        no-padding
        to="/"
      >
        {{ t('general.action.returnToHome') }}
      </XLink>

      <div :class="$style.info">
        <p :class="$style.description">
          {{ t('accessTerm.display.password_info') }}
          <XLink
            no-padding
            to="https://ajuda.clicksign.com/article/347-como-validar-um-documento-impresso"
            target="_blank"
          >
            {{ t('accessTerm.display.more_info') }}
          </XLink>
        </p>
        <XButton
          :class="$style.actionButton"
          data-testid="validateAnotherDocumentButton"
          @click="handlePagePrint"
        >
          <XIcon
            icon="print"
            left
          />
          {{ t('accessTerm.display.print_access_term') }}
        </XButton>
      </div>
    </header>

    <CompanyInfo
      :document="validDocument"
      :class="$style.companyInfo"
    />

    <p :class="$style.termDetails">
      {{ t('accessTerm.display.original_eletronic_info') }}
    </p>

    <CertifyInfo
      :valid-document="validDocument"
      :border-bottom="true"
    />

    <footer :class="$style.footer">
      <div :class="$style.howToAccess">
        <h4 :class="$style.title">
          {{ t('accessTerm.display.how_to_access') }}
        </h4>
        <p :class="$style.description">
          {{ t('accessTerm.display.instruction') }}
        </p>
      </div>

      <div :class="$style.container">
        <h4 :class="$style.title">
          {{ t('accessTerm.display.how_to_access_document') }}
        </h4>
        <div :class="$style.passwordData">
          <div :class="$style.retangle">
            <h5 :class="$style.retangleTitle">
              {{ t('accessTerm.display.password') }}
            </h5>

            <div
              :class="$style.password"
              data-testid="passwordText"
            >
              {{ validDocument.access_token }}
            </div>
          </div>
          <ol :class="$style.passwordList">
            <li>
              {{ t('accessTerm.display.access_validator') }}
              <XLink
                no-padding
                to="/"
              >
                {{ t('accessTerm.display.validator_site') }}
              </XLink>
            </li>
            <li>{{ t('accessTerm.display.password_validator') }}</li>
            <li>{{ t('accessTerm.display.type_password') }}</li>
          </ol>
        </div>
      </div>
      <div :class="$style.container">
        <h4 :class="$style.title">
          {{ t('accessTerm.display.how_to_access_via_qrcode') }}
        </h4>
        <div :class="$style.qrCodeData">
          <div :class="$style.retangle">
            <h5 :class="$style.retangleTitle">
              {{ t('accessTerm.display.qrcode_access') }}
            </h5>

            <div>
              <VueQrcode
                :value="fillPasswordUrl"
                :options="{ width: 162 }"
              />
            </div>
          </div>
          <p>
            {{ t('accessTerm.display.qrcode_description') }}
          </p>
        </div>
      </div>
    </footer>
  </section>
</template>

<style lang="scss" module>
.documentValidatorResult {
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  max-width: 100%;

  .header {
    width: 100%;
    margin-bottom: var(--space-large);

    .info {
      margin-top: var(--space-small-x);
      padding: var(--space-regular);
      background-color: var(--color-brand-support-02-50);
      border-radius: var(--border-radius-medium);

      .description {
        font-size: var(--font-size-regular);
        color: var(--color-neutral-1000);
      }
    }

    @media print {
      display: none;
    }
  }

  .companyInfo {
    align-self: center;
  }

  .termDetails {
    margin: 0px;
    margin-bottom: var(--space-small-x);
    font-size: var(--font-size-small);
    color: var(--color-neutral-1000);
  }

  .footer {
    display: flex;
    align-items: center;
    flex-direction: column;
    margin-top: var(--space-medium);
    gap: var(--space-medium);

    .howToAccess {
      display: flex;
      align-items: flex-start;
      flex-direction: column;
      width: 100%;

      .description {
        margin-bottom: 0px;
        font-size: var(--font-size-small);
        color: var(--color-neutral-1000);
      }
    }

    .title {
      margin-bottom: var(--space-small-xx);
      font-size: var(--font-size-regular);
      color: var(--color-neutral-1000);
    }

    .retangle {
      display: flex;
      align-items: center;
      justify-content: center;
      flex-direction: column;
      padding: var(--space-small-xx) var(--space-regular);
      border: var(--border-width-small) solid var(--color-neutral-1000);
      border-radius: var(--border-radius-medium);

      .retangleTitle {
        margin-bottom: 0;
        font-size: var(--font-size-small);
        color: var(--color-neutral-1000);
      }
    }

    .container {
      display: flex;
      align-items: flex-start;
      flex-direction: column;
      width: 100%;
      gap: var(--space-small-xx);

      @media print {
        page-break-inside: avoid;
      }
    }

    .passwordData {
      display: flex;
      align-self: flex-start;
      gap: var(--space-large-x);

      .password {
        font-weight: var(--font-weight-bold);
        text-transform: uppercase;
        letter-spacing: 5px;
      }

      .passwordList {
        padding: 0px;
      }
    }

    .qrCodeData {
      display: flex;
      align-self: flex-start;
      width: 100%;
      gap: var(--space-medium);
    }
  }
}
</style>
